import { authentication } from '@ecosystem/newron-client-lib';
import { getImpersonationHeader } from './impersonation';
import { Context } from '../types/content/context.types';
import { overlays } from '@ecosystem/newron-design-system';
import { PageContextType } from '../page/provider';

/*
 * Represents the request body structure for triggering the Qualtrics upsell survey in BFF
 */
interface QualtricsReqBody {
  /**Market identifier.*/
  market: string;
  /**Country code.*/
  country: string;
  /**Name of the client.*/
  clientName: string;
  /**ID of the client.*/
  clientId: number;
  /**User group IDs (optional).*/
  userGroupIds?: string;
  /**User group names (optional).*/
  userGroupNames?: string;
  /**Key (optional).*/
  key?: string;
  /**User ID (optional).*/
  userId?: string;
  /**Email address (optional).*/
  email?: string;
  /**Package name (optional).*/
  package?: string;
  /**Page name (optional).*/
  page?: string;
}

type UpsellStatus = 'upsell-shown' | 'upsell-activated' | 'upsell-declined';
interface UpsellAnalyticsReqBody {
  cell: string;
  upsellName: string;
  upsellStatus: UpsellStatus;
  dataPeriod?: string;
}

export enum UpgradeType {
  KPI_UPGRADE = 'KPI_UPGRADE',
  FEATURE_UPGRADE = 'FEATURE_UPGRADE',
  BRANDS_PAGE_UPGRADE = 'UNLOCK_BRANDS_PAGE',
  FEATURES_PAGE_UPGRADE = 'UNLOCK_FEATURES_PAGE',
  PRICE_CLASSES_PAGE_UPGRADE = 'UNLOCK_PRICE_CLASSES_PAGE',
  CHANNELS_PAGE_UPGRADE = 'UNLOCK_CHANNELS_PAGE',
  FIRST_ACTIVITY_PAGE_UPGRADE = 'UNLOCK_FIRST_ACTIVITY_PAGE',
  PRODUCT_LEADERBOARD_PAGE_UPGRADE = 'UNLOCK_PRODUCT_LEADERBOARD_PAGE',
  FEATURE_OR_KPI_UPGRADE = 'FEATURE_OR_KPI_UPGRADE',
}

/**
 * Triggers the Qualtrics upsell survey when the user clicks on the upgrade/upsell link.
 *
 * @param pageContext The context of the page where the upsell is triggered.
 * @param upsellContext The upsell context containing the Qualtrics URL and token.
 * @returns json response from the Qualtrics API.
 */
export const getUpsellQualtrics = async <T>(
  pageContext: Context,
  corePageState: PageContextType,
  upgradeType: UpgradeType
): Promise<T> => {
  const { upsellContext, page, context } = corePageState;
  const token = await authentication.getAccessToken();
  const impersonationHeader = getImpersonationHeader();
  const authUser = authentication.getUser();
  const currentSelectedMarket = pageContext.settings.values.cell[0].value;
  const country = currentSelectedMarket.split('-')[0].toUpperCase();
  const pageName = context.url
    .split('/')
    .pop()
    .concat(page ? `-${page.name}` : '')
    .concat(`-${upgradeType}`);

  const qualtricsReqBody: QualtricsReqBody = {
    market: currentSelectedMarket, // this needs to be dynamic based on the user's market
    country: country, // this needs to be dynamic based on the user's country
    clientName: authUser.clientName || null,
    clientId: authUser.clientId || null,
    userGroupIds: authUser.userGroupIds?.join(',') || null,
    email: authUser.email || null,
    page: pageName,
  };
  const upsellQualtricsResult = await fetch(upsellContext.upsellQualtricsURL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...impersonationHeader,
    },
    body: JSON.stringify(qualtricsReqBody),
  });
  return (await upsellQualtricsResult.json()) as T;
};

export const postUpsellAnalytics = async <T>(pageContext: Context, corePageState: PageContextType): Promise<T> => {
  const { upsellContext } = corePageState;
  const token = await authentication.getAccessToken();
  const impersonationHeader = getImpersonationHeader();
  const currentSelectedMarket = pageContext.settings.values.cell[0].value;
  const dataPeriod = pageContext.settings.values.timeFrame[0].label;
  const upsellName = 'reduced_content_upsell';
  const upsellAnalyticsReqBody: UpsellAnalyticsReqBody = {
    cell: currentSelectedMarket,
    upsellName: upsellName,
    upsellStatus: 'upsell-activated',
    dataPeriod,
  };
  const upsellAnalyticsResults = await fetch(upsellContext.upsellAnalyticsURL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...impersonationHeader,
    },
    body: JSON.stringify(upsellAnalyticsReqBody),
  });
  return (await upsellAnalyticsResults.json()) as T;
};

type ToastParams = { type: overlays.Notification; message: string };

export const triggerUpsellQualtrics = (
  context: Context,
  setToastFn: (params: ToastParams) => void,
  corePageState: PageContextType,
  upgradeType: UpgradeType
) => {
  getUpsellQualtrics(context, corePageState, upgradeType)
    .then(() => {
      postUpsellAnalytics(context, corePageState);
      setToastFn({
        type: 'success',
        message: 'Thank you for your request. We will be in touch with more information',
      });
    })
    .catch(err => {
      setToastFn({
        type: 'error',
        message: `Error on qualtrics request: ${err.message}`,
      });
    });
};
